import PostRequest from "csi/comm/PostRequest";
import monitor from "csi/monitor/_base";
import { URL_HIGG } from "csi/reflection/reflectionConstants";
import { resolveNode } from "csi/service/nodeService";
import { ReactionStore } from "csi/utility/mobxUtility";
import { sessionStore } from "csi/state/rootStore";
import WorkingCache from "csi/state/WorkingCache";
import { C8Node, ICache } from "csi/typings";
import xml from "csi/xml";
import Writer from "csi/xml/Writer";
import { action, makeObservable, observable, runInAction } from "mobx";
import { ChangeEvent, createContext, useContext } from "react";

const HIGG_API_KEY = "HiggAPIKey";
const HIGG_CONNECT = "HiggConnect";
const HIGG_PROGRESS_NODE_URL = "centric://REFLECTION/INSTANCE/HiggProgress";

let higgProgressChangeNodeHandle: dojo.Handle;

/** add higg api key to higg global singleton node, this API key already has flag NOT_TRANSFERRABLE */
async function addApiKeyToHiggGlobalSingletonNode(key: string): Promise<void> {
	const w = new Writer();
	const cn = w.changeNode(URL_HIGG);
	cn.changeAttribute(HIGG_API_KEY, xml.ATTR_STRING, key);
	try {
		await sessionStore.submitWriter(w);
	} catch (err) {
		console.warn("Failed to save Higg Api Key to the C8 Node.", err);
	}
}

/** get higg progress percentage from node */
function getHiggProgressPercentage(node: C8Node): number {
	if (node.TotalHiggCategories && node.TotalHiggMaterials) {
		const loadedData = node.LoadedHiggCategories + node.LoadedHiggMaterials;
		const totalData = node.TotalHiggCategories + node.TotalHiggMaterials;
		return loadedData >= totalData ? 100 : (loadedData / totalData) * 100;
	} else {
		return 0;
	}
}

/** get higg progress status from node */
function getHiggProgressStatus(node: C8Node): boolean {
	return node.IsHiggLoading;
}

/** get higg error flag from progress node */
function getHiggProgressError(node: C8Node): boolean {
	return node.IsHiggError;
}

/** download higg categories and higg materials from higg servers */
async function initializeHigg(): Promise<void> {
	try {
		await new PostRequest(HIGG_CONNECT, "SystemUserInitializeHigg").go();
	} catch (err) {
		throw "SystemUserInitializeHigg failed: " + err;
	}
}

/** delete higg categories and higg materials and reset the higg progress singleton node */
async function resetHigg(): Promise<void> {
	try {
		await new PostRequest(HIGG_CONNECT, "SystemUserResetHigg").go();
	} catch (err) {
		throw "SystemUserResetHigg failed: " + err;
	}
}

export class HiggSetupStore extends ReactionStore {
	@observable apiKeyValue: string = "";
	@observable apiTextInputValue: string = "";
	@observable higgCategoryCount: number = 0;
	@observable higgLoadProgress: number = 0;
	@observable isApiKeyValidationFailed: boolean = false;
	@observable isDialogOpen: boolean = false;
	@observable isHiggError: boolean = false;
	@observable initializeHiggLoading: boolean = false;
	@observable isResponseLoading: boolean = false;

	cache: ICache;

	get apiKeyExistsOnNode(): boolean {
		return !!this.apiKeyValue;
	}

	get disableInitializeButton(): boolean {
		return this.initializeHiggLoading || !!this.higgLoadProgress;
	}

	get higgDoneLoading(): boolean {
		return this.higgLoadProgress === 100;
	}

	get isConnectButtonDisabled(): boolean {
		return this.isResponseLoading || this.apiTextInputValue === this.apiKeyValue;
	}

	get showHiggIndeterminateLoader(): boolean {
		return this.initializeHiggLoading && this.higgLoadProgress === 0;
	}

	@action onApiTextInputChange = (event: ChangeEvent<HTMLInputElement>): void => {
		this.apiTextInputValue = event.target.value;
	};

	@action onOpenApiDialog = (): void => {
		const node = this.cache.get(URL_HIGG);
		this.apiKeyValue = this.apiTextInputValue = node.HiggAPIKey;
		this.isApiKeyValidationFailed = false;
		this.isDialogOpen = true;
	};

	@action setDialogClosed = (): void => {
		this.isDialogOpen = false;
	};

	@action setResponseLoading = (loading: boolean): void => {
		this.isResponseLoading = loading;
	};

	/** callback to update the progress, staying in sync with the higg progress node */
	@action updateHiggProgress = (node: C8Node): void => {
		this.higgLoadProgress = getHiggProgressPercentage(node);
		this.initializeHiggLoading = getHiggProgressStatus(node);
		this.isHiggError = getHiggProgressError(node);
	};

	/** if there is a higg progress handle, remove it */
	cleanupHiggProgressChangeNodeHandle = (): void => {
		higgProgressChangeNodeHandle?.remove();
		higgProgressChangeNodeHandle = null;
	};

	/** initialize a node monitor to keep track of progress changes */
	initializeNodeMonitor = async (): Promise<void> => {
		const {
			cache,
			updateHiggProgress,
		} = this;

		const higgProgressNode = await resolveNode(HIGG_PROGRESS_NODE_URL, cache);
		if (!higgProgressChangeNodeHandle) {
			higgProgressChangeNodeHandle = monitor.onChangeNode(higgProgressNode.$URL, (higgProgressNode) => updateHiggProgress(higgProgressNode));
		}

		// manually call the update progress function for cases where it won't be called
		updateHiggProgress(higgProgressNode);
	};

	/** instantly set the higg categories and materials to true even before the server does and start the process */
	initializeHiggCategoriesAndMaterials = async (): Promise<void> => {
		try {
			runInAction(() => {
				this.initializeHiggLoading = true;
			});

			// start the entire initialize process on the server
			await initializeHigg();
		} catch (err) {
			console.error("Higg initialization failed", err);
		}
	};

	/** initialize the setup store, set the api key and check for node progress */
	initialzeHiggSetupStore = async (): Promise<void> => {
		const higgGlobalNode = this.cache.get(URL_HIGG);
		runInAction(() => {
			this.apiKeyValue = this.apiTextInputValue = higgGlobalNode.HiggAPIKey;
		});

		// initialize the monitor when loading the page
		this.initializeNodeMonitor();

		// when leaving the page, make sure to clean up the change node monitor
		this.trackReaction(this.cleanupHiggProgressChangeNodeHandle);
	};

	onConnect = async (): Promise<void> => {
		const { apiTextInputValue, setResponseLoading } = this;
		setResponseLoading(true);
		const verified = await this.verifyApiKey(apiTextInputValue);
		runInAction(() => {
			this.isResponseLoading = false;
			if (!verified) {
				this.isApiKeyValidationFailed = true;
				return;
			}
			this.apiKeyValue = apiTextInputValue;
			this.isDialogOpen = false;
			resetHigg();
		});
	};

	verifyApiKey = async (key: string): Promise<boolean> => {
		try {
			const req = await new PostRequest(HIGG_CONNECT, "ValidateAPIKey")
				.addParameter("Key", key)
				.go();

			const { data } = req;

			if (data === 200) {
				await addApiKeyToHiggGlobalSingletonNode(key);
				return true;
			} else {
				return false;
			}
		} catch (err) {
			console.error(err);
			return false;
		}
	};

	constructor(cache: ICache = new WorkingCache()) {
		super();
		this.cache = cache;
		makeObservable(this);
		this.initialzeHiggSetupStore();
	}
}

export const HiggSetupStoreContext = createContext<HiggSetupStore>(undefined);
export const useHiggSetupStore = (): HiggSetupStore => useContext(HiggSetupStoreContext);